import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { usePost, useCreatePost, useUpdatePost } from '@/hooks/usePosts';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Save, Eye, Code, Eye as VisualIcon } from 'lucide-react';
import WysiwygEditor from '@/components/admin/WysiwygEditor';

const postSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  slug: z.string().min(1, 'Slug is required').regex(/^[a-z0-9-]+$/, 'Slug can only contain lowercase letters, numbers, and hyphens'),
  excerpt: z.string().optional(),
  content: z.string().optional(),
  status: z.enum(['draft', 'published']),
  meta_title: z.string().optional(),
  meta_description: z.string().optional(),
});

type PostFormData = z.infer<typeof postSchema>;

const PostEditor = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const isEditing = !!id;
  const [editorMode, setEditorMode] = useState<'visual' | 'code'>('visual');

  const { data: post, isLoading } = usePost(id || '');
  const createPost = useCreatePost();
  const updatePost = useUpdatePost();

  const form = useForm<PostFormData>({
    resolver: zodResolver(postSchema),
    defaultValues: {
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      status: 'draft',
      meta_title: '',
      meta_description: '',
    },
  });

  useEffect(() => {
    if (post && isEditing) {
      form.reset({
        title: post.title,
        slug: post.slug,
        excerpt: post.excerpt || '',
        content: post.content || '',
        status: post.status as 'draft' | 'published',
        meta_title: post.meta_title || '',
        meta_description: post.meta_description || '',
      });
    }
  }, [post, isEditing, form]);

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const onSubmit = async (data: PostFormData) => {
    if (!user) return;

    try {
      const postData = {
        ...data,
        author_id: user.id,
        publish_date: data.status === 'published' ? new Date().toISOString() : null,
      };

      if (isEditing && id) {
        await updatePost.mutateAsync({ id, ...postData });
        toast({
          title: "Success",
          description: "Post updated successfully",
        });
      } else {
        await createPost.mutateAsync(postData);
        toast({
          title: "Success",
          description: "Post created successfully",
        });
      }
      
      navigate('/admin/posts');
    } catch (error) {
      toast({
        title: "Error",
        description: `Failed to ${isEditing ? 'update' : 'create'} post`,
        variant: "destructive",
      });
    }
  };

  if (isLoading && isEditing) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-muted rounded w-1/4 animate-pulse"></div>
        <Card className="animate-pulse">
          <CardHeader>
            <div className="h-6 bg-muted rounded w-1/3"></div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="h-10 bg-muted rounded"></div>
            <div className="h-32 bg-muted rounded"></div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">
          {isEditing ? 'Edit Post' : 'Create New Post'}
        </h1>
        <div className="flex gap-2">
          {isEditing && post?.status === 'published' && (
            <Button variant="outline" asChild>
              <a href={`/blog/${post.slug}`} target="_blank" rel="noopener noreferrer">
                <Eye className="mr-2 h-4 w-4" />
                Preview
              </a>
            </Button>
          )}
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Tabs defaultValue="content" className="space-y-6">
            <TabsList>
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="seo">SEO & Meta</TabsTrigger>
            </TabsList>

            <TabsContent value="content" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Post Content</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <FormField
                      control={form.control}
                      name="title"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Title</FormLabel>
                          <FormControl>
                            <Input 
                              placeholder="Enter post title"
                              {...field}
                              onChange={(e) => {
                                field.onChange(e);
                                if (!isEditing) {
                                  const slug = generateSlug(e.target.value);
                                  form.setValue('slug', slug);
                                }
                              }}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="slug"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>URL Slug</FormLabel>
                          <FormControl>
                            <Input placeholder="url-friendly-slug" {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  <FormField
                    control={form.control}
                    name="excerpt"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Excerpt</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="Brief description of the post"
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="content"
                    render={({ field }) => (
                      <FormItem>
                        <div className="flex items-center justify-between">
                          <FormLabel>Content</FormLabel>
                          <div className="flex gap-2">
                            <Button
                              type="button"
                              variant={editorMode === 'visual' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setEditorMode('visual')}
                            >
                              <VisualIcon className="mr-2 h-4 w-4" />
                              Visual
                            </Button>
                            <Button
                              type="button"
                              variant={editorMode === 'code' ? 'default' : 'outline'}
                              size="sm"
                              onClick={() => setEditorMode('code')}
                            >
                              <Code className="mr-2 h-4 w-4" />
                              Code
                            </Button>
                          </div>
                        </div>
                        <FormControl>
                          {editorMode === 'visual' ? (
                            <WysiwygEditor
                              value={field.value}
                              onChange={field.onChange}
                              placeholder="Write your post content here..."
                            />
                          ) : (
                            <Textarea 
                              placeholder="Write your post content here. You can use HTML tags like <h1>, <h2>, <p>, <strong>, <em>, <ul>, <ol>, <li>, <blockquote>, etc."
                              className="min-h-[400px] font-mono"
                              {...field}
                            />
                          )}
                        </FormControl>
                        <FormMessage />
                        <p className="text-sm text-muted-foreground">
                          {editorMode === 'visual' 
                            ? 'Use the visual editor to format your content with a WYSIWYG interface.'
                            : 'You can use HTML tags for formatting. Supported tags include: h1, h2, h3, h4, h5, h6, p, strong, em, ul, ol, li, blockquote, code, pre, br, and more.'
                          }
                        </p>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="status"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Status</FormLabel>
                        <Select onValueChange={field.onChange} defaultValue={field.value}>
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder="Select status" />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="draft">Draft</SelectItem>
                            <SelectItem value="published">Live</SelectItem>
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="seo" className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>SEO & Meta Tags</CardTitle>
                </CardHeader>
                <CardContent className="space-y-6">
                  <FormField
                    control={form.control}
                    name="meta_title"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meta Title</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder="SEO title (recommended: 50-60 characters)"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                        <p className="text-sm text-muted-foreground">
                          If left empty, the post title will be used. Keep under 60 characters for best SEO results.
                        </p>
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="meta_description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Meta Description</FormLabel>
                        <FormControl>
                          <Textarea 
                            placeholder="SEO description (recommended: 150-160 characters)"
                            className="min-h-[100px]"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage />
                        <p className="text-sm text-muted-foreground">
                          If left empty, the excerpt will be used. Keep between 150-160 characters for best SEO results.
                        </p>
                      </FormItem>
                    )}
                  />
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>

          <div className="flex justify-end gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/admin/posts')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createPost.isPending || updatePost.isPending}
            >
              <Save className="mr-2 h-4 w-4" />
              {isEditing ? 'Update Post' : 'Create Post'}
            </Button>
          </div>
        </form>
      </Form>
    </div>
  );
};

export default PostEditor;